/**
 * Draws an Environment's screen-space effects into its scene, for each render through whichever
 * camera renders it, while its Environment is on the scene (`sceneEnvironment.tsx`).
 */
import { useEffect, useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import { projectAmbientOcclusionSettings, projectDrawsDepthPrepass, projectTracesHalfSizeReflections, } from '../../parser/projectSettingsParser.js';
import { useProjectSettings } from '../contexts/ProjectSettingsContext.js';
import { observeSceneCamera, observeSceneRenderEnd } from '../sceneRenderCamera.js';
import { registerEnvironmentPart, useEnvironmentKey } from '../environment/sceneEnvironment.js';
import { SceneScreenSpaceEffects } from './sceneScreenSpaceEffects.js';
export function ScreenSpaceEffectsLayer({ ssr, ssao }) {
    const gl = useThree((state) => state.gl);
    const scene = useThree((state) => state.scene);
    const invalidate = useThree((state) => state.invalidate);
    const environment = useEnvironmentKey();
    const { settings: projectSettings } = useProjectSettings();
    const isHalfSizeReflection = useMemo(() => projectTracesHalfSizeReflections(projectSettings), [projectSettings]);
    const ssaoProject = useMemo(() => projectAmbientOcclusionSettings(projectSettings), [projectSettings]);
    const drawsDepthPrepass = useMemo(() => projectDrawsDepthPrepass(projectSettings), [projectSettings]);
    // Read at each render, so a new setting reaches the next render with each buffer's history kept.
    const current = {
        reflection: ssr && { ssr, isHalfSize: isHalfSizeReflection },
        occlusion: ssao && { ssao, project: ssaoProject },
        drawsDepthPrepass,
    };
    const settings = useRef(current);
    settings.current = current;
    useEffect(() => {
        const effects = new SceneScreenSpaceEffects(gl, () => settings.current, invalidate);
        const sentinels = [effects.firstSentinel, effects.lastSentinel];
        const showSentinels = (isShown) => sentinels.forEach((sentinel) => (sentinel.visible = isShown));
        // Shown, not added, by each pass: an add would tell every tracker of the scene that it changed.
        showSentinels(false);
        scene.add(...sentinels);
        const stopRegistering = registerEnvironmentPart(scene, environment, () => {
            showSentinels(true);
            const stopBeginning = observeSceneCamera(scene, effects.begin);
            const stopEnding = observeSceneRenderEnd(scene, effects.end);
            return () => {
                stopBeginning();
                stopEnding();
                showSentinels(false);
            };
        });
        return () => {
            stopRegistering();
            scene.remove(...sentinels);
            effects.dispose();
        };
    }, [gl, scene, environment, invalidate]);
    return null;
}
//# sourceMappingURL=ScreenSpaceEffectsLayer.js.map